/**
 * Work-day checklist helpers for the calendar create/edit forms.
 *
 * Company policy: middle days run 8 AM – 6 PM. First day uses the user's
 * chosen start time → 6 PM. Last day uses 8 AM → the user's chosen end
 * time. Sat/Sun auto-unchecked on generation, user can re-check. Only
 * produces a multi-day list when the chosen date span crosses midnight.
 */

export const MIDDLE_START_HOUR = 8;
export const MIDDLE_END_HOUR = 18; // 6 PM
export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const dayKey = (d) =>
  `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * Enumerate every calendar day in [startMs, endMs]. Returns empty array
 * when both fall on the same day (no multi-day UI needed).
 */
export const generateDayList = (startMsStr, endMsStr) => {
  const sNum = parseInt(startMsStr);
  const eNum = parseInt(endMsStr);
  if (!sNum || !eNum || sNum > eNum) return [];
  const s = new Date(sNum);
  const e = new Date(eNum);
  const startDay = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  if (startDay.getTime() === endDay.getTime()) return [];
  const list = [];
  const cur = new Date(startDay);
  while (cur.getTime() <= endDay.getTime()) {
    const weekday = cur.getDay();
    list.push({
      key: dayKey(cur),
      year: cur.getFullYear(),
      month: cur.getMonth(),
      day: cur.getDate(),
      weekday,
      isWeekend: weekday === 0 || weekday === 6,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return list;
};

export const timeOfDay = (ms) => {
  if (!ms) return null;
  const n = parseInt(ms);
  if (isNaN(n)) return null;
  const d = new Date(n);
  return { h: d.getHours(), m: d.getMinutes() };
};

const composeMs = (year, month, day, h, m) =>
  new Date(year, month, day, h, m, 0, 0).getTime().toString();

/**
 * Build the workDays payload (server-facing) from the list of checked
 * day descriptors in date order. Returns [] when there's ≤1 day checked —
 * the server falls back to parent startMs/endMs for single-day jobs.
 */
export const buildWorkDaysPayload = (checkedDays, formStartMs, formEndMs) => {
  if (checkedDays.length <= 1) return [];
  const startTod = timeOfDay(formStartMs) || { h: MIDDLE_START_HOUR, m: 0 };
  const endTod = timeOfDay(formEndMs) || { h: MIDDLE_END_HOUR, m: 0 };
  return checkedDays.map((d, i) => {
    const isFirst = i === 0;
    const isLast = i === checkedDays.length - 1;
    const sH = isFirst ? startTod.h : MIDDLE_START_HOUR;
    const sM = isFirst ? startTod.m : 0;
    const eH = isLast ? endTod.h : MIDDLE_END_HOUR;
    const eM = isLast ? endTod.m : 0;
    return {
      startMs: composeMs(d.year, d.month, d.day, sH, sM),
      endMs: composeMs(d.year, d.month, d.day, eH, eM),
    };
  });
};

const fmtClock = (h, m) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mm = m.toString().padStart(2, "0");
  return `${h12}:${mm} ${ampm}`;
};

/** Human-readable time range for a day entry given form start/end inputs. */
export const previewDayTimes = (isFirst, isLast, formStartMs, formEndMs) => {
  const startTod = timeOfDay(formStartMs) || { h: MIDDLE_START_HOUR, m: 0 };
  const endTod = timeOfDay(formEndMs) || { h: MIDDLE_END_HOUR, m: 0 };
  const sH = isFirst ? startTod.h : MIDDLE_START_HOUR;
  const sM = isFirst ? startTod.m : 0;
  const eH = isLast ? endTod.h : MIDDLE_END_HOUR;
  const eM = isLast ? endTod.m : 0;
  return `${fmtClock(sH, sM)} → ${fmtClock(eH, eM)}`;
};

/**
 * Build initial `checkedKeys` Set for EditEventModal when a record already
 * has a workDays array. Matches each stored workDay to a generated list
 * entry by (year, month, day). New list entries that don't match any
 * stored workDay fall back to the Sat/Sun auto-skip default.
 */
export const seedCheckedKeysFromWorkDays = (list, existingWorkDays) => {
  const checked = new Set();
  const stored = new Set(
    (existingWorkDays || []).map((wd) => {
      const n = parseInt(wd.startMs);
      if (isNaN(n)) return null;
      const d = new Date(n);
      return dayKey(d);
    }).filter(Boolean)
  );
  for (const entry of list) {
    if (stored.has(entry.key) || (stored.size === 0 && !entry.isWeekend)) {
      checked.add(entry.key);
    }
  }
  return checked;
};
